import React, {useEffect} from 'react';
import {useAsyncEffect} from 'use-async-effect';
import {connect} from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {ConnectedRouter as Router} from 'connected-react-router';
import {ToastContainer} from 'react-toastify';
import LoadingScreen from 'react-loading-screen';
import moment from 'moment';
import 'moment/locale/en-au';

import './world/css/bootstrap.min.css';
import './world/style.css';
import './App.css';
import 'react-datetime/css/react-datetime.css';
import 'react-toastify/dist/ReactToastify.css';

import {AppState, history} from './Store';
import {AuthState} from './Store/Auth/Types';

import {
  loginCreator,
  openDevToolsCreator,
  logout,
  crash,
  setDebug,
} from './Store/Auth/Actions';
import {initialiseHome} from './Store/Home/Actions';
import {setUserCreator} from './Store/User/Actions';
import {loadPriorityModulesCreator} from './Store/Home/Actions';

import {Home as HomeModel} from './Services/HomeService';

import Public from './Public';
import Admin from './Admin';
import Tickets from './Tickets';
import Roadmap from './Roadmap';

moment.locale('en-au');

const promiseFinally = require('promise.prototype.finally');
promiseFinally.shim();

interface AppProps {
  authorised: boolean;
  appCrash: boolean;
  debug: boolean;

  login: () => Promise<AuthState>;
  loadPriorityModules: () => Promise<string[]>;
  openDevTools: () => Promise<void>;
  initialiseHome: typeof initialiseHome;
  setUser: (username?: string) => Promise<void>;
  crash: typeof crash;
  setDebug: typeof setDebug;
}

function App({
  authorised,
  appCrash,
  debug,
  login,
  loadPriorityModules,
  openDevTools,
  initialiseHome,
  setUser,
  crash,
  setDebug,
}: AppProps) {
  useAsyncEffect(async isMounted => {
    const {
      homeService,
      officeCompanionService,
      admin,
    } = await login();

    setDebug(await officeCompanionService.getDebug());

    try {
      const modules = await loadPriorityModules();
      const home = await homeService.home(modules);
      if (home.parsedBody !== undefined && isMounted) {
        initialiseHome(home.parsedBody);
      }
    } catch (e) {
      crash();
    }

    if (admin) {
      const username = await officeCompanionService.getUsername();
      setUser(username);
    }
  }, [login, initialiseHome, setUser]);

  useEffect(() => {
    if (authorised) {
      return;
    }

    const timeout = setTimeout(() => {
      crash();
    }, 5000);

    return () => clearTimeout(timeout);
  }, [authorised, crash]);

  const loadingText = appCrash ? "Sorry for the inconvenience. We'll be back up and running as fast as possible." : 'Connectx is loading';

  return (
    <LoadingScreen
      loading={appCrash || !authorised}
      bgColor="#f1f1f1"
      spinnerColor="#9ee5f8"
      textColor="#676767"
      logoSrc="/logo.png"
      text={loadingText}
    >
      <div className="App">
        <ToastContainer
          position="top-center"
          autoClose={2500}
          newestOnTop
          closeOnClick
        />
        {debug
          ? <a className="float-left" href="/" onClick={() => openDevTools()}>Open Dev Tools&nbsp;</a>
          : null
        }
        <Router history={history}>
          <Switch>
            <Redirect exact from="/" to="/public" />
            <Route path="/public" component={Public} />
            <Route path="/admin" component={Admin} />
            <Route path="/tickets" component={Tickets} />
            <Route path="/roadmap" component={Roadmap} />
          </Switch>
        </Router>
      </div>
    </LoadingScreen>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    authorised: state.auth.authorised,
    appCrash: state.auth.appCrash,
    debug: state.auth.debug,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    login: () => dispatch(loginCreator()),
    loadPriorityModules: () => dispatch(loadPriorityModulesCreator()),
    logout: () => dispatch(logout),
    initialiseHome: (home: HomeModel) => dispatch(initialiseHome(home)),
    setUser: (username?: string) => dispatch(setUserCreator(username)),
    crash: () => dispatch(crash()),
    setDebug: (debug: boolean) => dispatch(setDebug(debug)),
    openDevTools: () => dispatch(openDevToolsCreator()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
